// ============================================================
// Braval - Main App Component
// ============================================================

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig';
import { apiFetch } from './api';
import SearchBar from './components/SearchBar';
import FiltersSidebar from './components/FiltersSidebar';
import ResultsGrid from './components/ResultsGrid';
import LandingPage from './components/LandingPage';
import EditLotPage from './components/EditLotPage';
import NewLotPage from './components/NewLotPage';
import AuctionsPage from './components/AuctionsPage';
import AuctionViewPage from './components/AuctionViewPage';
import EditAuctionPage from './components/EditAuctionPage';
import NewAuctionPage from './components/NewAuctionPage';
import './App.css';

export const msalInstance = new PublicClientApplication(msalConfig);

// ------------------------------------------------------------
// Sign In Page (shown when not authenticated)
// ------------------------------------------------------------
function SignInPage() {
    const { instance } = useMsal();

const handleLogin = () => {
        instance.loginRedirect(loginRequest).catch(console.error);
    };


    return (
        <div className="signin-page">
            <div className="signin-box">
                <h1>Branford Valuations</h1>
                <p>Auction Comps Database</p>
                <button className="signin-button" onClick={handleLogin}>
                    Sign in with Microsoft
                </button>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// DB Status indicator — polls /api/health every 30 seconds
// ------------------------------------------------------------
function DbStatus() {
    const [status, setStatus] = useState(null); // null | 'green' | 'yellow' | 'red'

    useEffect(() => {
        const check = async () => {
            if (document.hidden) return;
            try {
                const res = await apiFetch('/api/health');
                if (!res.ok) throw new Error();
                const data = await res.json();
                setStatus(data.db ? 'green' : 'yellow');
            } catch {
                setStatus('red');
            }
        };
        check();
        const interval = setInterval(check, 30000);
        return () => clearInterval(interval);
    }, []);

    if (!status) return null;

    const labels = { green: 'Database Online', yellow: 'Database Offline', red: 'API Offline' };

    return (
        <span className={`db-status db-status-${status}`} title={labels[status]}>
            <span className="db-status-dot" />
            <span className="db-status-label">{labels[status]}</span>
        </span>
    );
}

// ------------------------------------------------------------
// Header (shared across authenticated pages)
// ------------------------------------------------------------
export function Header() {
    const { instance, accounts } = useMsal();
    const userName = accounts[0]?.name || accounts[0]?.username || '';

const handleLogout = () => {
        instance.logoutRedirect().catch(console.error);
    };

    return (
        <header className="app-header">
            <div className="header-left">
                <Link to="/" className="header-title-link">
                    <h1>Branford Valuations</h1>
                </Link>
                <nav className="header-nav">
                    <Link to="/auctions" className="header-nav-link">Auction Database</Link>
                    <Link to="/comps" className="header-nav-link">Comps Database</Link>
                </nav>
            </div>
            <div className="header-right">
                <DbStatus />
                <span className="header-user">{userName}</span>
                <button className="signout-button" onClick={handleLogout}>Sign out</button>
            </div>
        </header>
    );
}

// ------------------------------------------------------------
// Comps Page — the auction comps search tool
// ------------------------------------------------------------
function CompsPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        projectName: '',
        auctioneer: '',
        dataSource: '',
        auctionYearFrom: '',
        auctionYearTo: '',
        itemYearFrom: '',
        itemYearTo: '',
        includeNullItemYear: false,
        make: '',
        priceMin: '',
        priceMax: '',
    });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState(null);

    const handleSearch = async () => {
        setLoading(true);
        setHasSearched(true);
        setSearchError(null);
        try {
            const params = new URLSearchParams();
            if (searchTerm)          params.append('search', searchTerm);
            if (filters.projectName)          params.append('projectName', filters.projectName);
            if (filters.auctioneer)           params.append('auctioneer', filters.auctioneer);
            if (filters.dataSource)           params.append('dataSource', filters.dataSource);
            if (filters.make)                 params.append('make', filters.make);
            if (filters.auctionYearFrom)      params.append('auctionYearFrom', filters.auctionYearFrom);
            if (filters.auctionYearTo)        params.append('auctionYearTo', filters.auctionYearTo);
            if (filters.itemYearFrom)         params.append('itemYearFrom', filters.itemYearFrom);
            if (filters.itemYearTo)           params.append('itemYearTo', filters.itemYearTo);
            if (filters.priceMin)             params.append('priceMin', filters.priceMin);
            if (filters.priceMax)             params.append('priceMax', filters.priceMax);
            params.append('includeNullItemYear', filters.includeNullItemYear);
            const response = await apiFetch(`/api/search?${params.toString()}`);
            const data = await response.json();
            if (!response.ok) {
                setSearchError(data.error || 'An error occurred.');
                setResults([]);
            } else {
                setResults(data);
            }
        } catch (err) {
            console.error('Search error:', err);
            setSearchError('Could not reach the API. Please try again.');
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-layout">
            <Header />
            <div className="app-body">
                <aside className="sidebar">
                    <FiltersSidebar filters={filters} setFilters={setFilters} onSearch={handleSearch} />
                </aside>
                <main className="main-content">
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        onSearch={handleSearch}
                        loading={loading}
                    />
                    <ResultsGrid
                        results={results}
                        loading={loading}
                        hasSearched={hasSearched}
                        error={searchError}
                    />
                </main>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Root App — wraps everything in MSAL provider + router
// ------------------------------------------------------------
export default function App() {
    return (
        <MsalProvider instance={msalInstance}>
            <AuthenticatedTemplate>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/auctions" element={<AuctionsPage />} />
                        <Route path="/auctions/new" element={<NewAuctionPage />} />
                        <Route path="/auctions/:id" element={<AuctionViewPage />} />
                        <Route path="/auctions/:id/edit" element={<EditAuctionPage />} />
                        <Route path="/comps" element={<CompsPage />} />
                        <Route path="/lots/new" element={<NewLotPage />} />
                        <Route path="/lots/:id" element={<EditLotPage />} />
                    </Routes>
                </BrowserRouter>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <SignInPage />
            </UnauthenticatedTemplate>
        </MsalProvider>
    );
}
